import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingOverlay from "react-loading-overlay";
import {
  Input,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Form,
  FormGroup,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoading,
  fetchJobApplicationList,
  rejectJobApplicationById,
  updateJobApplicationStatusById,
  fetchCandidateIdByEmail,
  setCandidateId,
} from "../../store/actions";
import {
  useTableHook,
  DynamicTableHelper,
  DateHelper,
} from "@workspace/common";
import * as FileHelper from "@workspace/common/src/helpers/file_helper";
import FilePreview from "@workspace/common/src/Components/FilePreview/FilePreview";
import { tagJob } from "@workspace/job/src/store/actions";
import useImportCandidate from "@workspace/candidate/src/pages/CandidateResumeParse/useImportCandidate";
import * as BackendHelper from "../../helpers/backend_helper";
import DynamicTableWrapper from "../DynamicTableWrapper/DynamicTableWrapper";
import { resetApproveCreateSuccess } from "@workspace/candidate/src/store/actions";
import { resetApproveTagSuccess } from "@workspace/job/src/store/actions";
import {
  JOB_APP_INITIAL_OPTIONS,
  JOB_APP_MANDATORY_OPTIONS,
} from "./PulseApplicationContentConstants";
import "./PulseApplicationContent.scss";

export default function PulseApplicationContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { importCandidate } = useImportCandidate();
  const hasPageRendered = useRef(false);
  const [tableConfig, setTableConfig] = useState([]);
  const [activeJob, setActiveJob] = useState([]);
  const [gridView, setGridView] = useState("");
  const [jobAppCustomView, setJobAppCustomView] = useState(null);
  const [jobAppFilterBubble, setJobAppFilterBubble] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [actionData, setActionData] = useState(null);
  const [approveModal, setApproveModal] = useState(false);
  const [candidateData, setCandidateData] = useState(null);
  const [candidateFile, setCandidateFile] = useState(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [canvasType, setCanvasType] = useState("file");
  const [videoBlob, setVideoBlob] = useState(null);

  const toggleOffcanvas = () => {
    setOffcanvasOpen(!offcanvasOpen);
    setVideoBlob(null);
  };

  // Get data from Redux store
  const jobAppDataRaw = useSelector(
    (state) => state.JobApplicationReducer.jobApplicationList
  );
  const jobAppDefaultCustomView = useSelector(
    (state) => state.JobApplicationReducer.jobAppDefaultCustomView
  );
  const allJobAppCustomViews = useSelector(
    (state) => state.JobApplicationReducer.jobAppCustomViews
  );
  const loading = useSelector((state) => state.JobApplicationReducer.loading);
  const candidateIdByEmail = useSelector(
    (state) => state.JobApplicationReducer.candidateId
  );
  const approveCreateSuccess = useSelector(
    (state) => state.CandidateReducer.approveCreateSuccess
  );
  const approveTagSuccess = useSelector(
    (state) => state.JobStageReducer.approveTagSuccess
  );

  // Get the job application data
  const jobAppData = useMemo(() => jobAppDataRaw?.content, [jobAppDataRaw]);

  // CustomView - Custom renders
  const customRenderList = [
    {
      names: ["status"],
      render: (data, opt) => (
        <Badge
          className={`text-dark text-uppercase ${getClassBasedOnValue(
            data.status
          )}`}
        >
          {DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"}
        </Badge>
      ),
    },
    {
      names: ["jobType"],
      render: (data, opt) => (
        <Badge className="text-dark text-uppercase bg-blackcoral">
          {DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"}
        </Badge>
      ),
    },
    {
      names: ["jobTitle"],
      render: (data) => {
        return (
          <>
            <Link
              to={`/jobs/${data.jobDbId}/snapshot`}
              className="text-custom-primary text-decoration-underline"
            >
              <span>{data?.jobTitle}</span>
            </Link>
          </>
        );
      },
    },
    {
      names: ["resumeName"],
      render: (data) => {
        return (
          <>
            <Button className="cv-btn" onClick={() => handleOpenResume(data)}>
              <i
                className="mdi mdi-file-document-outline fs-20"
                style={{ color: "#0A4FAE" }}
              />
            </Button>
          </>
        );
      },
    },
    {
      names: ["dateApplied"],
      render: (data, opt) =>
        DateHelper.formatDateTimeStandard(
          DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"
        ),
    },
  ];

  // DynamicTable - Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    handleRowCheck,
    selectAllRows,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "desc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
    },
    JOB_APP_MANDATORY_OPTIONS,
    JOB_APP_INITIAL_OPTIONS,
    customRenderList
  );

  // DynamicTable - Set table config
  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateJobListConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateJobListConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // DynamicTable - Fetch the job when the pageRequest changes
  useEffect(() => {
    const request = {
      ...pageRequest,
      status: gridView,
      filters: jobAppFilterBubble,
    };

    if (pageRequest?.searchFields?.length > 0 && jobAppFilterBubble != null) {
      setActiveRow([]);
      dispatch(
        fetchJobApplicationList(DynamicTableHelper.cleanPageRequest(request))
      );
    }
  }, [pageRequest, gridView, jobAppFilterBubble]);

  // DynamicTable - Reset the page when the gridView changes
  useEffect(() => {
    if (hasPageRendered.current) {
      pageRequestSet.setPage(0);
    }
    hasPageRendered.current = true;
  }, [gridView]);

  // DynamicTable - Update the page info when job Data changes
  useEffect(() => {
    if (jobAppDataRaw) {
      setPageInfoData(jobAppDataRaw);
    }
    setTableData(jobAppDataRaw?.content);
  }, [jobAppDataRaw]);

  // CustomView - Set custom view state
  useEffect(() => {
    if (allJobAppCustomViews != null && allJobAppCustomViews?.length > 0) {
      const selectedCustomView = allJobAppCustomViews?.find(
        (customView) => customView?.selected
      );
      if (selectedCustomView) {
        setJobAppCustomView(selectedCustomView);
      } else {
        setJobAppCustomView(-1);
      }
    }
    if (allJobAppCustomViews != null && allJobAppCustomViews?.length === 0) {
      setJobAppCustomView(-1);
    }
  }, [allJobAppCustomViews]);

  // CustomFilter - Job Listing Bubble Filter Setting
  useEffect(() => {
    const isEmpty = (obj) => Object.entries(obj).length === 0;

    if (jobAppFilterBubble == null) {
      if (jobAppCustomView != null) {
        if (jobAppCustomView != -1) {
          setJobAppFilterBubble(jobAppCustomView?.filters);
        } else if (jobAppDefaultCustomView) {
          if (isEmpty(jobAppDefaultCustomView)) {
            setJobAppFilterBubble([]);
          } else {
            setJobAppFilterBubble(jobAppDefaultCustomView?.filters);
          }
        }
      }
    }
  }, [jobAppCustomView, jobAppDefaultCustomView]);

  // DynamicTable - Generate Table Header
  const generateJobListConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      {
        header: (
          <div className="form-check px-0">
            <Input
              className="form-check-input"
              type="checkbox"
              id="checkbox"
              checked={
                activeJob?.length > 0 &&
                activeJob?.length === jobAppDataRaw?.content?.length
              }
              onChange={(e) => selectAllJobs(e?.target?.checked)}
            />
          </div>
        ),
        name: "checkbox",
        sort: false,
        render: (data) => {
          return (
            <div className="form-check px-0">
              <Input
                className="form-check-input"
                type="checkbox"
                checked={activeJob.includes(parseInt(data?.id))}
                onChange={(e) => handleJobCheck(data?.id, e.target.checked)}
              />
            </div>
          );
        },
      },
      ...customConfig,
      {
        header: "Action",
        name: "action",
        sort: false,
        sortValue: "action",
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => {
          return (
            <>
              <Button
                className="mx-2 approve-btn"
                title="Approve"
                onClick={() => handleApproveModal(data)}
                disabled={data?.status !== "new"}
              >
                <i className="mdi mdi-check" />
              </Button>
              <Button
                className="ms-2 reject-btn"
                title="Reject"
                onClick={() => handleRejectModal(data)}
                disabled={data?.status !== "new"}
              >
                <i className="mdi mdi-close" />
              </Button>
            </>
          );
        },
      },
    ];
  };

  // DynamicTable - Select all jobs
  const selectAllJobs = (isChecked) => {
    selectAllRows(isChecked);
    if (isChecked) {
      if (jobAppDataRaw?.content) {
        const ids = [];
        jobAppDataRaw?.content?.forEach((item) => {
          ids.push(item?.id);
        });
        setActiveJob(ids);
      }
    } else {
      setActiveJob([]);
    }
  };

  // DynamicTable - Handle job check
  const handleJobCheck = (id, checked) => {
    handleRowCheck(id, checked);
    if (checked) {
      setActiveJob([...activeJob, id]);
    } else {
      const updatedVal = activeJob?.filter((sid) => sid !== id);
      setActiveJob(updatedVal);
    }
  };

  // DynamicTable - Handle table view change
  const handleTableViewChange = (e) => {
    setGridView(e.target.value);
    setActiveJob([]);
  };

  // Action - Handle open resume
  const handleOpenResume = async (data) => {
    try {
      dispatch(setLoading(true));
      let documentData = null;
      const response = await BackendHelper.getJobApplicationResumeById(
        data?.id
      );
      documentData = response;
      const ext = documentData?.fileName.split(".").pop();

      if (ext == "docx" || ext == "doc") {
        // Convert to formdata
        const formData = new FormData();
        formData.append(
          "docFile",
          FileHelper.base64ToFile(
            documentData?.encodedFile,
            documentData?.fileName
          )
        );

        const convertedData = await BackendHelper.convertMsDocToPdf(formData);
        const docData = convertedData.data;
        const pdfName = docData?.fileName?.split(".")[0] + ".pdf";
        const file = await FileHelper.base64ToFile(
          docData?.encodedFile,
          pdfName
        );
        setFilePreview(file);
        setCanvasType("file");
        setOffcanvasOpen(true);
        return;
      }

      if (ext == "pdf") {
        const file = FileHelper.base64ToFile(
          documentData?.encodedFile,
          documentData?.fileName
        );
        setFilePreview(file);
        setCanvasType("file");
        setOffcanvasOpen(true);
        return;
      }

      toast.error("File format not supported for preview.");
    } catch (e) {
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Action - Handle approve modal
  const handleApproveModal = (data) => {
    setActionData(data);
    setApproveModal(true);
  };

  // Action - Handle approve submit
  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    setApproveModal(false);
    try {
      dispatch(fetchCandidateIdByEmail(actionData?.email));

      let documentData = null;
      const response = await BackendHelper.getJobApplicationResumeById(
        actionData?.id
      );
      documentData = response;
      const fileName = documentData?.fileName;
      const fileNameNoExt = fileName.split(".").slice(0, -1).join(".");

      const fileObjectRaw = FileHelper.base64ToFile(
        documentData?.encodedFile,
        documentData?.fileName
      );
      const fileObject = {
        file: fileObjectRaw,
        name: fileName,
        nameNoExt: fileNameNoExt,
      };

      setCandidateData([JSON.parse(actionData?.candidateData)]);
      setCandidateFile([fileObject]);
    } catch (error) {
      console.error(
        "There was an error in handleApproveModal function: ",
        error
      );
    }
  };

  // Execute when candidateIdByEmail is fetched to import and tag the candidate
  useEffect(() => {
    if (
      candidateIdByEmail == null &&
      candidateData != null &&
      candidateFile != null
    ) {
      importCandidate(candidateData, candidateFile);
    }

    if (
      candidateIdByEmail != null &&
      candidateIdByEmail !== "" &&
      candidateData != null &&
      candidateFile != null
    ) {
      const payload = {
        jobId: `${actionData?.jobDbId}`,
        jobStageId: 1,
        status: "COMPLETED",
        candidateId: candidateIdByEmail,
        stepName: "Profile",
        subStepName: "Tag",
      };
      dispatch(
        updateJobApplicationStatusById({
          id: actionData?.id,
          status: "approved",
        })
      );
      dispatch(tagJob({ payload, navigate, approveAction: true }));
    }
  }, [candidateIdByEmail, candidateData, candidateFile]);

  /* Execute when approveSuccess is true to 
  fetch candidateIdByEmail after creating candidate */
  useEffect(() => {
    if (approveCreateSuccess) {
      dispatch(fetchCandidateIdByEmail(actionData?.email));
      dispatch(resetApproveCreateSuccess());
    }
  }, [approveCreateSuccess]);

  /* Execute when approveTagSuccess is true to reset states and
  fetch job application list after tagging the candidate */
  useEffect(() => {
    if (approveTagSuccess) {
      const request = {
        ...pageRequest,
        status: gridView,
        filters: jobAppFilterBubble,
      };
      dispatch(setLoading(false));
      dispatch(setCandidateId(""));
      setCandidateData(null);
      setCandidateFile(null);
      setActionData(null);
      dispatch(
        fetchJobApplicationList(DynamicTableHelper.cleanPageRequest(request))
      );
      dispatch(resetApproveTagSuccess());
    }
  }, [approveTagSuccess]);

  // // Handle reject modal
  const handleRejectModal = (data) => {
    setActionData(data);
    setRejectModal(true);
  };

  // Handle reject submit
  const handleRejectSubmit = (e) => {
    e.preventDefault();
    setRejectModal(false);
    const reasonText = e.target.text.value;
    const rejectionData = {
      id: actionData.id,
      rejectionReason: reasonText,
    };
    dispatch(rejectJobApplicationById(rejectionData));
    dispatch(
      updateJobApplicationStatusById({ id: actionData.id, status: "rejected" })
    );
    setTimeout(() => {
      const request = {
        ...pageRequest,
        status: gridView,
        filters: jobAppFilterBubble,
      };
      dispatch(
        fetchJobApplicationList(DynamicTableHelper.cleanPageRequest(request))
      );
      dispatch(setLoading(false));
    }, 2000);
  };

  // Status styling - Function to get class based on status value
  const getClassBasedOnValue = (value) => {
    if (value === "-") {
      return "text-muted";
    } else if (value === "approved") {
      return "badge-approved";
    } else if (value === "rejected") {
      return "badge-rejected";
    } else {
      return "badge-new";
    }
  };

  return (
    <LoadingOverlay
      active={loading}
      spinner
      text="Please wait..."
      className="pulseapp-container"
    >
      <DynamicTableWrapper
        data={jobAppData ?? []}
        config={tableConfig}
        pageInfo={pageInfo}
        pageRequest={pageRequest}
        pageRequestSet={pageRequestSet}
        search={search}
        setSearch={setSearch}
        setCustomConfigData={setCustomConfigData}
        gridView={gridView}
        handleTableViewChange={handleTableViewChange}
        setTableConfig={setTableConfig}
        activeRow={activeRow}
        // setActiveRow={setActiveRow}
        jobAppCustomView={jobAppCustomView}
        setJobAppCustomView={setJobAppCustomView}
        jobAppFilterBubble={jobAppFilterBubble}
        setJobAppFilterBubble={setJobAppFilterBubble}
      />
      <Modal isOpen={approveModal} toggle={() => setApproveModal(false)}>
        <Form onSubmit={handleApproveSubmit}>
          <ModalHeader toggle={() => setApproveModal(false)}>
            Approve Candidate
          </ModalHeader>
          <ModalBody>
            <p className="mb-0">
              Are you sure you want to{" "}
              <span className="text-success">APPROVE</span> this candidate?
            </p>
            <small>
              Candidate will be created and tagged to the job indicated below.
            </small>
            <ListGroup className="mt-3">
              <ListGroupItem>
                Candidate Name:{" "}
                <b>
                  {actionData?.firstName} {actionData?.lastName}
                </b>
              </ListGroupItem>
              <ListGroupItem>
                Candidate Email Address: <b>{actionData?.email}</b>
              </ListGroupItem>
              <ListGroupItem>
                Job Title: <b>{actionData?.jobTitle}</b>
              </ListGroupItem>
              <ListGroupItem>
                Pulse Job Id: <b>{actionData?.jobId}</b>
              </ListGroupItem>
              <ListGroupItem>
                Job Account Name: <b>{actionData?.accountName}</b>
              </ListGroupItem>
            </ListGroup>
            <div className="d-flex gap-2 justify-content-end mt-3 action-modal">
              <Button
                className="cancel-btn"
                onClick={() => setApproveModal(false)}
              >
                Cancel
              </Button>
              <Button color="success" type="submit">
                Confirm
              </Button>
            </div>
          </ModalBody>
        </Form>
      </Modal>
      <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)}>
        <Form onSubmit={handleRejectSubmit}>
          <ModalHeader toggle={() => setRejectModal(false)}>
            Reject Candidate
          </ModalHeader>
          <ModalBody>
            <p className="mb-0">
              Are you sure you want to{" "}
              <span className="text-danger">REJECT</span> this candidate?
            </p>
            <ListGroup className="my-3">
              <ListGroupItem>
                Candidate Name:{" "}
                <b>
                  {actionData?.firstName} {actionData?.lastName}
                </b>
              </ListGroupItem>
              <ListGroupItem>
                Candidate Email Address: <b>{actionData?.email}</b>
              </ListGroupItem>
              <ListGroupItem>
                Job Title: <b>{actionData?.jobTitle}</b>
              </ListGroupItem>
              <ListGroupItem>
                Pulse Job Id: <b>{actionData?.jobId}</b>
              </ListGroupItem>
              <ListGroupItem>
                Job Account Name: <b>{actionData?.accountName}</b>
              </ListGroupItem>
            </ListGroup>
            <FormGroup>
              <Label for="rejectText">
                Reasons/Comments<span className="text-danger">*</span>
              </Label>
              <Input
                id="rejectText"
                name="text"
                type="textarea"
                placeholder="Enter Reason"
                rows="5"
                required
              />
            </FormGroup>
            <div className="d-flex gap-2 justify-content-end action-modal">
              <Button
                className="cancel-btn"
                onClick={() => setRejectModal(false)}
              >
                Cancel
              </Button>
              <Button color="success" type="submit">
                Confirm
              </Button>
            </div>
          </ModalBody>
        </Form>
      </Modal>
      <Offcanvas
        className="file-offcanvas"
        isOpen={offcanvasOpen}
        toggle={toggleOffcanvas}
        direction="end"
      >
        {canvasType === "file" && (
          <>
            <OffcanvasHeader toggle={toggleOffcanvas}>
              File Preview
            </OffcanvasHeader>
            <OffcanvasBody>
              <FilePreview file={filePreview} />
            </OffcanvasBody>
          </>
        )}
      </Offcanvas>
    </LoadingOverlay>
  );
}
